'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { Pagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { memo, useMemo, useState } from 'react';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client/useTableSettings';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { getVaultUrl } from '@/helpers/routing';
import type { VaultListingOutput } from './vaultListing.contract';
import { VAULT_LISTING_DEFAULT_TABLE_SETTINGS, VAULT_LISTING_STORAGE_KEY } from './config/config';
import { columnLabelsVaultListing, columnsMarketAllocation, vaultListingTableFeatures } from './config/VaultListing.columns';
import { getVaultListingRows } from './getVaultListingRows';
import VaultListingLayout from './VaultListingLayout';
export interface VaultListingClientProps {
  chainId: ChainId;
  vaultListing: VaultListingOutput;
}

/**
 * Vault listing table component to display each vault  that supply the market
 *
 */
const VaultListingClient = t0 => {
  const $ = _c(37);
  const {
    chainId,
    vaultListing: t1
  } = t0;
  const {
    items,
    partial,
    truncated
  } = t1;
  const chainIdentifier = InterfaceChainUtils.getIdentifierById(chainId);
  let t2;
  if ($[0] !== items) {
    t2 = getVaultListingRows(items);
    $[0] = items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const vaultListing = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      pageKey: PageKey.Market,
      tableSettingsObjectKey: VAULT_LISTING_STORAGE_KEY,
      defaultTableSettings: VAULT_LISTING_DEFAULT_TABLE_SETTINGS
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    sorting,
    setSorting
  } = useTableSettings(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      pageIndex: 0,
      pageSize: 5
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [pagination, setPagination] = useState(t4);
  let t5;
  if ($[4] !== pagination || $[5] !== sorting) {
    t5 = {
      sorting,
      pagination
    };
    $[4] = pagination;
    $[5] = sorting;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== setSorting || $[8] !== t5 || $[9] !== vaultListing) {
    t6 = {
      features: vaultListingTableFeatures,
      data: vaultListing,
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
      state: t5,
      columns: columnsMarketAllocation,
      enableSortingRemoval: false,
      sortDescFirst: true
    };
    $[7] = setSorting;
    $[8] = t5;
    $[9] = vaultListing;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const vaultListingTable = useTable(t6);
  const T0 = VaultListingLayout;
  let t7;
  if ($[11] !== partial || $[12] !== truncated) {
    t7 = (partial || truncated) && <p data-testid="vault-listing-partial-notice" className="text-secondary-foreground px-md text-sm">Some vaults could not be loaded, so this list may be incomplete.</p>;
    $[11] = partial;
    $[12] = truncated;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const T1 = TableScrollArea;
  const t8 = "grid h-full w-full rounded-[20px] [&_td:first-of-type]:w-1/2 [&_td:nth-of-type(2)]:w-auto";
  const t9 = vaultListingTable.getRowModel().rows.length > 0 && <Table.Table tableVariant="compact"><Table.Thead>{vaultListingTable.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{vaultListingTable.getRowModel().rows.map(row => <TrBodyRow key={row.id} row={row} href={getVaultUrl({
        identifier: chainIdentifier,
        vaultAddress: row.original.vault.address,
        vaultName: row.original.vault.name
      })} />)}</Table.Tbody></Table.Table>;
  let t10;
  if ($[14] !== vaultListingTable) {
    t10 = vaultListingTable.getRowModel().rows.length === 0 && <TableNoResults text="No vault found." height="640px" style={{
      borderRadius: "0"
    }} />;
    $[14] = vaultListingTable;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== T1 || $[17] !== t10 || $[18] !== t9) {
    t11 = <T1 className={t8}>{t9}{t10}</T1>;
    $[16] = T1;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = vaultListing.length;
  const t13 = vaultListingTable.previousPage;
  const t14 = vaultListingTable.nextPage;
  const t15 = vaultListingTable.setPageIndex;
  let t16;
  if ($[20] !== vaultListingTable) {
    t16 = vaultListingTable.getCanPreviousPage();
    $[20] = vaultListingTable;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== vaultListingTable) {
    t17 = vaultListingTable.getCanNextPage();
    $[22] = vaultListingTable;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== pagination || $[25] !== t16 || $[26] !== t17 || $[27] !== vaultListing.length || $[28] !== vaultListingTable.nextPage || $[29] !== vaultListingTable.previousPage || $[30] !== vaultListingTable.setPageIndex) {
    t18 = <Pagination loading={false} pagination={pagination} totalItemsCount={t12} previousPage={t13} nextPage={t14} setPage={t15} canPreviousPage={t16} canNextPage={t17} />;
    $[24] = pagination;
    $[25] = t16;
    $[26] = t17;
    $[27] = vaultListing.length;
    $[28] = vaultListingTable.nextPage;
    $[29] = vaultListingTable.previousPage;
    $[30] = vaultListingTable.setPageIndex;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== T0 || $[33] !== t11 || $[34] !== t18 || $[35] !== t7) {
    t19 = <T0>{t7}{t11}{t18}</T0>;
    $[32] = T0;
    $[33] = t11;
    $[34] = t18;
    $[35] = t7;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
};
export default memo(VaultListingClient);
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} columnLabels={columnLabelsVaultListing} headerGroup={headerGroup} />;
}